import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { vouchersApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { AuditTimeline } from '../../components/shared/AuditTimeline';
import { VoucherPrintTemplate } from '../../components/voucher/VoucherPrintTemplate';
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  getPaymentMethodLabel,
  getPaymentTypeLabel,
} from '../../lib/utils';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  DollarSign,
  FileText,
  Download,
  Building2,
  User,
  Calendar,
  CreditCard,
  Banknote,
  Printer,
  Upload,
  X,
  PauseCircle,
  PlayCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

export const AccountsProcessPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [voucher, setVoucher] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [actionDialog, setActionDialog] = useState({ open: false, type: null });
  const [note, setNote] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [paidAttachment, setPaidAttachment] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [voucherData, logsData] = await Promise.all([
          vouchersApi.getById(id),
          vouchersApi.getAuditLogs(id),
        ]);
        setVoucher(voucherData);
        setAuditLogs(logsData);
      } catch (error) {
        toast.error('Voucher not found');
        navigate('/accounts/queue');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  const handleAction = async () => {
    setProcessing(true);
    try {
      let updated;
      if (actionDialog.type === 'approve') {
        updated = await vouchersApi.accountsApprove(id, note);
        toast.success('Voucher approved and voucher number generated');
      } else if (actionDialog.type === 'reject') {
        updated = await vouchersApi.accountsReject(id, note);
        toast.success('Voucher rejected');
      } else if (actionDialog.type === 'hold') {
        updated = await vouchersApi.holdVoucher(id, note);
        toast.success('Voucher has been put on hold');
      } else if (actionDialog.type === 'release') {
        updated = await vouchersApi.releaseHold(id, note);
        toast.success('Voucher has been released from hold');
      } else if (actionDialog.type === 'paid') {
        if (!paymentRef) {
          toast.error('Payment reference is required');
          setProcessing(false);
          return;
        }
        
        // Upload attachment if provided
        if (paidAttachment) {
          setUploadingAttachment(true);
          try {
            await vouchersApi.uploadPaymentAttachment(id, paidAttachment);
          } catch (uploadError) {
            console.error('Failed to upload attachment:', uploadError);
            // Continue with marking as paid even if attachment upload fails
          }
          setUploadingAttachment(false);
        }
        
        updated = await vouchersApi.markPaid(id, paymentRef, note);
        toast.success('Voucher marked as paid');
      }
      setVoucher(updated);
      const logs = await vouchersApi.getAuditLogs(id);
      setAuditLogs(logs);
      setActionDialog({ open: false, type: null });
      setNote('');
      setPaymentRef('');
      setPaidAttachment(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process voucher');
    } finally {
      setProcessing(false);
      setUploadingAttachment(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-48 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
          <div className="h-96 bg-gray-100 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (!voucher) return null;

  const attachments = voucher.attachments?.map((a) => {
    const [filename, originalName] = a.split('|');
    return { filename, originalName: originalName || filename };
  }) || [];

  const canApproveReject = voucher.status === 'INTERNAL_AUDIT_APPROVED';
  const canHold = voucher.status === 'INTERNAL_AUDIT_APPROVED' || voucher.status === 'ACCOUNTS_APPROVED';
  const canMarkPaid = voucher.status === 'ACCOUNTS_APPROVED';

  return (
    <div className="space-y-6" data-testid="accounts-process-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/accounts/queue')}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-heading font-semibold tracking-tight font-mono">
                {voucher.voucherNo || 'Pending Voucher No'}
              </h1>
              <StatusBadge status={voucher.status} />
            </div>
            <p className="text-muted-foreground text-sm mt-1">
              Created by {voucher.createdByName} • Manager: {voucher.assignedManagerName}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 print:hidden">
          <Button
            variant="outline"
            onClick={() => window.print()}
            data-testid="print-button"
          >
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
          {canApproveReject && (
            <Button
              variant="outline"
              onClick={() => setActionDialog({ open: true, type: 'reject' })}
              className="border-red-200 text-red-600 hover:bg-red-50"
              data-testid="reject-button"
            >
              <XCircle className="w-4 h-4 mr-2" /> Reject
            </Button>
          )}
          {canHold && (
            <Button
              variant="outline"
              onClick={() => setActionDialog({ open: true, type: 'hold' })}
              className="border-yellow-300 text-yellow-700 hover:bg-yellow-50"
              data-testid="hold-button"
            >
              <PauseCircle className="w-4 h-4 mr-2" /> Hold
            </Button>
          )}
          {canApproveReject && (
            <Button
              onClick={() => setActionDialog({ open: true, type: 'approve' })}
              className="bg-green-600 hover:bg-green-700"
              data-testid="approve-button"
            >
              <CheckCircle className="w-4 h-4 mr-2" /> Approve
            </Button>
          )}
          {voucher.status === 'HOLD' && (
            <Button
              onClick={() => setActionDialog({ open: true, type: 'release' })}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="release-hold-button"
            >
              <PlayCircle className="w-4 h-4 mr-2" /> Release Hold
            </Button>
          )}
          {canMarkPaid && (
            <Button
              onClick={() => setActionDialog({ open: true, type: 'paid' })}
              className="bg-[#6a5da4] hover:bg-[#003380]"
              data-testid="mark-paid-button"
            >
              <DollarSign className="w-4 h-4 mr-2" /> Mark as Paid
            </Button>
          )}
        </div>
      </div>

      {/* Manager Note */}
      {voucher.managerNote && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <p className="text-sm font-medium text-blue-800">Manager Note:</p>
            <p className="text-sm text-blue-700 mt-1">{voucher.managerNote}</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Employee & Manager Info */}
          <Card className="border-l-4 border-l-[#0047AB]">
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#6a5da4]/10 flex items-center justify-center">
                    <User className="w-6 h-6 text-[#6a5da4]" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Created By</p>
                    <p className="font-semibold">{voucher.createdByName}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Approved By</p>
                    <p className="font-semibold">{voucher.assignedManagerName}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Voucher Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Date</p>
                    <p className="font-medium">{formatDate(voucher.date)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Branch</p>
                    <p className="font-medium">{voucher.branch}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Department</p>
                    <p className="font-medium">{voucher.department}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Beneficiary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Beneficiary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Name</p>
                  <p className="font-medium">{voucher.beneficiaryName}</p>
                </div>
                {voucher.beneficiaryAddress && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Address</p>
                    <p className="font-medium">{voucher.beneficiaryAddress}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Payment Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Payment Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Method</p>
                    <p className="font-medium">{getPaymentMethodLabel(voucher.paymentMethod)}</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Type</p>
                  <p className="font-medium">{getPaymentTypeLabel(voucher.paymentType)}</p>
                </div>
                {(voucher.advancePercent !== null && voucher.advancePercent !== undefined) && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Advance %</p>
                    <p className="font-medium">{voucher.advancePercent}%</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Currency</p>
                  <p className="font-medium">{voucher.currency}</p>
                </div>
              </div>

              {voucher.paymentMethod === 'TRANSFER' && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Bank Details</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Bank Name</p>
                      <p className="font-medium">{voucher.bankName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">IBAN</p>
                      <p className="font-mono text-sm">{voucher.iban}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Line Items</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Document No.</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Invoice Amount</TableHead>
                    <TableHead className="text-right">Payable Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {voucher.items?.map((item, index) => (
                    <TableRow key={item.id || index}>
                      <TableCell className="font-mono">{item.invoiceNo || '-'}</TableCell>
                      <TableCell className="text-muted-foreground">{item.note || '-'}</TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(item.amount, item.currency)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(item.payableAmount || 0, item.currency)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-gray-50 font-semibold">
                    <TableCell colSpan={3}>Total Payable Amount</TableCell>
                    <TableCell className="text-right font-mono text-lg text-[#6a5da4]">
                      {formatCurrency(voucher.amountTotal, voucher.currency)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Attachments */}
          {attachments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading">Attachments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {attachments.map((attachment) => (
                    <div
                      key={attachment.filename}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                        <span className="text-sm">{attachment.originalName}</span>
                      </div>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${voucher.id}/${attachment.filename}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#6a5da4] hover:underline text-sm flex items-center gap-1"
                      >
                        <Download className="w-4 h-4" /> Download
                      </a>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Additional Attachments */}
          {voucher.additionalAttachments?.length > 0 && (
            <Card className="border-blue-200">
              <CardHeader>
                <CardTitle className="text-lg font-heading text-blue-700">Additional Attachments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {voucher.additionalAttachments.map((attachment, index) => {
                    const parts = attachment.split('|');
                    const filename = parts[0];
                    const originalName = parts[1] || filename;
                    const uploadedAt = parts[2];
                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-blue-600" />
                          <div>
                            <span className="text-sm font-medium">{originalName}</span>
                            {uploadedAt && (
                              <p className="text-xs text-muted-foreground">
                                Uploaded: {formatDateTime(uploadedAt)}
                              </p>
                            )}
                          </div>
                        </div>
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${voucher.id}/additional/${filename}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                        >
                          <Download className="w-4 h-4" /> Download
                        </a>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Amount Summary */}
          <Card className="bg-[#6a5da4] text-white">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <Banknote className="w-8 h-8 opacity-80" />
                <div>
                  <p className="text-sm opacity-80">Total Amount</p>
                  <p className="text-3xl font-mono font-semibold">
                    {formatCurrency(voucher.amountTotal, voucher.currency)}
                  </p>
                </div>
              </div>
              {voucher.amountWords && (
                <p className="text-sm opacity-80 pt-4 border-t border-white/20">
                  {voucher.amountWords}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Payment Status */}
          {voucher.status === 'PAID' && (
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-3">
                  <DollarSign className="w-6 h-6 text-green-600" />
                  <span className="font-semibold text-green-800">Payment Completed</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div>
                    <p className="text-green-700/70">Paid At</p>
                    <p className="font-medium text-green-800">{formatDateTime(voucher.paidAt)}</p>
                  </div>
                  <div>
                    <p className="text-green-700/70">Payment Reference</p>
                    <p className="font-mono font-medium text-green-800">{voucher.paymentReference}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Audit Log */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Activity Log</CardTitle>
            </CardHeader>
            <CardContent>
              <AuditTimeline logs={auditLogs} />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => setActionDialog({ open, type: actionDialog.type })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionDialog.type === 'approve' && 'Approve Voucher'}
              {actionDialog.type === 'reject' && 'Reject Voucher'}
              {actionDialog.type === 'hold' && 'Put Voucher on Hold'}
              {actionDialog.type === 'release' && 'Release Voucher from Hold'}
              {actionDialog.type === 'paid' && 'Mark as Paid'}
            </DialogTitle>
            <DialogDescription>
              {actionDialog.type === 'approve' && 'A voucher number will be generated automatically.'}
              {actionDialog.type === 'reject' && 'This voucher will be marked as rejected by accounts.'}
              {actionDialog.type === 'hold' && 'This voucher will be put on hold and the employee will be notified.'}
              {actionDialog.type === 'release' && 'This voucher will be released from hold and resume its previous workflow.'}
              {actionDialog.type === 'paid' && 'Enter the payment reference to complete this voucher.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {actionDialog.type === 'paid' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="paymentRef">Payment Reference *</Label>
                  <Input
                    id="paymentRef"
                    value={paymentRef}
                    onChange={(e) => setPaymentRef(e.target.value)}
                    placeholder="Enter payment reference number"
                    data-testid="payment-ref-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="attachment">Attachment (Optional)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="attachment"
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file && file.size > 5 * 1024 * 1024) {
                          toast.error(`File "${file.name}" exceeds 5MB limit. Please select a smaller file.`);
                          e.target.value = '';
                          return;
                        }
                        setPaidAttachment(file || null);
                      }}
                      className="flex-1"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                      data-testid="paid-attachment-input"
                    />
                  </div>
                  {paidAttachment && (
                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded border text-sm">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <span className="flex-1 truncate">{paidAttachment.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => setPaidAttachment(null)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Supported: PDF, Images, Word, Excel (Max 5MB)
                  </p>
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="note">
                {actionDialog.type === 'reject' ? 'Rejection Reason *' : 
                 actionDialog.type === 'hold' ? 'Hold Reason *' :
                 actionDialog.type === 'release' ? 'Release Note (optional)' : 'Note (optional)'}
              </Label>
              <Textarea
                id="note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={
                  actionDialog.type === 'approve'
                    ? 'Add any comments...'
                    : actionDialog.type === 'reject'
                    ? 'Please explain why this voucher is being rejected...'
                    : actionDialog.type === 'hold'
                    ? 'Please explain why this voucher is being put on hold...'
                    : actionDialog.type === 'release'
                    ? 'Add any comments about the release...'
                    : 'Add payment notes...'
                }
                rows={3}
                data-testid="action-note"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setActionDialog({ open: false, type: null });
                setNote('');
                setPaymentRef('');
                setPaidAttachment(null);
              }}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAction}
              disabled={processing || ((actionDialog.type === 'reject' || actionDialog.type === 'hold') && !note) || (actionDialog.type === 'paid' && !paymentRef)}
              className={
                actionDialog.type === 'approve' 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : actionDialog.type === 'reject'
                  ? 'bg-red-600 hover:bg-red-700'
                  : actionDialog.type === 'hold'
                  ? 'bg-yellow-500 hover:bg-yellow-600'
                  : actionDialog.type === 'release'
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-[#6a5da4] hover:bg-[#003380]'
              }
              data-testid="confirm-action"
            >
              {processing ? 'Processing...' : 
                actionDialog.type === 'approve' ? 'Approve' : 
                actionDialog.type === 'reject' ? 'Reject' : 
                actionDialog.type === 'hold' ? 'Put on Hold' :
                actionDialog.type === 'release' ? 'Release' : 'Mark as Paid'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Template - Hidden on screen, visible on print */}
      <VoucherPrintTemplate voucher={voucher} />
    </div>
  );
};

export default AccountsProcessPage;
